import {
	formatDate,
	isSameOrAfter,
	isSameOrBefore,
} from "@sembark-travel/datetime-utils"
import * as Validator from "yup"
import { isTruthy } from "@sembark-travel/ui-form"
import type { TSearchParams } from "@sembark-travel/ui-list"
import type { IUser } from "../Users"

// Cancellation and payment policies are versioned the same way: a head carries
// the name, and every edit writes a new version under it. Each kind only adds
// its own rules to the version.

export type TPolicyVersionBase = {
	id: number
	head_name?: string
	head_id: number
	additional_info: string | null
	version: number
	created_at: string
	updated_at: string
	created_by: IUser
	updated_by: IUser | null
}

// The shape returned by the versions listing endpoint and embedded in a rate
// sheet block: the version without its rules or audit users — those come from
// `getVersionById`.
export type TPolicyVersionSummary = Pick<
	TPolicyVersionBase,
	"id" | "head_id" | "additional_info" | "version" | "created_at" | "updated_at"
>

export type TPolicyHead<TPolicy extends TPolicyVersionBase> = {
	id: number
	name: string
	is_default: boolean
	latest_policy: TPolicy
	created_at: string
	updated_at: string
	created_by: IUser
	updated_by: IUser | null
	deleted_at: string | null
}

export type TPolicyHeadSummary = {
	id: number
	name: string
	latest_policy: TPolicyVersionSummary
}

export type TPolicyListFilters = TSearchParams & {
	deleted_only?: boolean
}

export type TPolicyListQuery = TSearchParams & {
	deleted_only?: 1
}

export function policyFiltersToQuery(
	filters: TPolicyListFilters
): TPolicyListQuery {
	const { deleted_only, ...others } = filters
	const query: TPolicyListQuery = { ...others }
	if (isTruthy(deleted_only)) {
		query.deleted_only = 1
	}
	return query
}

export function policyQueryToFilters(
	query: TPolicyListQuery
): TPolicyListFilters {
	const { deleted_only, ...others } = query
	const filters: TPolicyListFilters = { ...others }
	if (isTruthy(deleted_only)) {
		filters.deleted_only = true
	}
	return filters
}

export function policyFiltersToRequestParams(filters: TPolicyListFilters) {
	const { deleted_only, ...otherParams } = filters
	return {
		...otherParams,
		deleted_only: deleted_only ? 1 : undefined,
	}
}

// ─── Date Range Rule Utilities ────────────────────────────────────────────────

export type TDateRange = {
	valid_from: Date | null
	valid_to: Date | null
}

export const dateRangeSchema = Validator.object().shape({
	valid_from: Validator.date()
		.nullable()
		.typeError("Please select a valid date")
		.required("Start date is required"),
	valid_to: Validator.date()
		.nullable()
		.typeError("Please select a valid date")
		.required("End date is required")
		.min(
			Validator.ref("valid_from"),
			"End date must be on or after start date"
		),
})

export function findOverlappingDateRange(
	dates: TDateRange | undefined,
	ranges?: Array<TDateRange>
): TDateRange | undefined {
	const from = dates?.valid_from
	const to = dates?.valid_to
	if (!from || !to) return undefined
	return (ranges || []).find(
		(range) =>
			range.valid_from &&
			range.valid_to &&
			isSameOrBefore(from, range.valid_to, "day") &&
			isSameOrAfter(to, range.valid_from, "day")
	)
}

export function formatDateRange(from?: Date | null, to?: Date | null): string {
	const start = from ? formatDate(from, "DD MMM, YYYY") : "–"
	const end = to ? formatDate(to, "DD MMM, YYYY") : "–"
	return `${start} → ${end}`
}
